/**
 * Notification Service
 *
 * Shared notification service for the entire application — Supabase is the
 * only implementation (mock mode was removed app-wide).
 *
 * Used by: Calendar, To-Do, Albums, and any feature that sends notifications.
 *
 * @module shared/services/notificationService
 */

import type { INotificationService } from '@/shared/types/notification.types';
import { SupabaseNotificationService } from './supabaseNotificationService';

/**
 * Notification service singleton instance — Supabase-backed.
 *
 * A `link` is stored VERBATIM and handed straight to the router when the
 * notification is tapped — there is no substitution step and no validation on
 * the way in. So build it from `ROUTE_PATHS`, never from a hand-typed literal:
 * this app has already shipped two notifications that opened NotFound, and both
 * came from a literal ('/calendar', '/love') that looked plausible and was not
 * a route. `getPath` is the cheapest way to make that mistake impossible; see
 * `NOTIFICATION_LINKS_WRITTEN_BY_BACKEND` in `shared/constants/routes` for the
 * links a trigger or an edge function writes, which cannot import it.
 *
 * @example
 * ```typescript
 * import { getPath, RouteKey } from '@/shared/constants/routes';
 * import { notificationService } from '@/shared/services/notificationService';
 *
 * // Get all notifications
 * const notifications = await notificationService.getNotifications();
 *
 * // Add a notification from another feature
 * await notificationService.addNotification({
 *   type: 'event',
 *   title: 'Ново събитие',
 *   message: 'Събитието "Парти" беше създадено',
 *   data: { eventId: 'event-123' },
 *   link: getPath(RouteKey.FAMILY_CALENDAR),
 * });
 * ```
 */
export const notificationService: INotificationService =
  new SupabaseNotificationService();

// Named export for testing and direct access
export { SupabaseNotificationService };
